// generateKolonSemaMultiFloor.js
// Çok Katlı Tek Sayaçlı Projeler için Kolon Şeması
import { Rectangle, PolyLine, Circle, Line, PolarLine, Arc, Text } from "./utils"
import {
  AnahtarliOtomatikSigorta,
  BulasikMak,
  CamasirMak,
  CihazBox,
  Firin,
  KacakAkim,
  Mak,
  PanoTali,
  Priz,
  Lamba,
  Anahtar,
  YuvarlakSolid,
  Sayac,
  PanoAnaTali,
  Boru
} from "./symbols"
import { createTekHatSema, TEK_HAT_BASE_DATA } from "./generateTekHatSema"

// Benzersiz ID sayacı
let textIdCounter = 0;

// Sabit değerler
const SABIT_DEGERLER = {
  // Tek Hat Schema'dan tüm base veriler alınıyor
  ...TEK_HAT_BASE_DATA,
  // ========== KOLON ŞEMASINA ÖZEL OVERRIDE'LAR ==========
  // Panel adları
  panelNames: ["MUTFAK PANOSU"],
  // Koruma röleleri
  protectionRelays: {
    zkt: "Z.K.T.",     // Zeminde kaçak akım tertibatı
    nktCount: 7        // Nötrde kaçak akım tertibatı sayısı
  },
  grounding: [
    "ANA SU BORUSU",
    "BİNA GİRİŞ DAĞITIM PANOSU",
    //"ASANSÖR RAYLARI",
    //"KABLO MERDİVENİ",
    "P.d. iletkeni en az 16 mm² NYAF",
  ]
};

function calculateDynamicPositions(itemCount, Scl) {
  const positions = [];
  const textSizes = [];
  const startOffset = -45 * Scl;
  const spacing = -20 * Scl;

  for (let i = 0; i < itemCount; i++) {
    positions.push(startOffset + (i * spacing));

    // Text boyutları - son iki eleman küçük
    if (i >= itemCount - 2 && itemCount > 2) {
      textSizes.push(9 * Scl);
    } else {
      textSizes.push(10 * Scl);
    }
  }

  return { positions, textSizes };
}


export const createKolonSemaMultiFloor = (data) => {
  let result = [];
  const Scl = 1;

  // ✅ Multi-floor data - mainPanel çıkışları (tek hat şeması çizilecek paneller)
  const mainPanelOutputs = data?.mainPanelOutputs || [];
  const allOutputs = mainPanelOutputs;

  const {
    branchies = [],     // Default empty array to prevent length errors
    feeder = null       // Default null for safe checking
  } = data || {};
  const headerHeight = 50 * Scl

  const cableText = data?.kolonDetails?.cable || "4x16 mm² NYY"
  const breakerText1 = data?.kolonDetails?.breaker?.text1 || "C-3x63A "
  const breakerText2 = data?.kolonDetails?.breaker?.text2 || "10kA"
  const rcdText1 = data?.kolonDetails?.rcd?.text1 || "63A 4P/300 mA"
  const rcdText2 = data?.kolonDetails?.rcd?.text2 || "Y.K'lı T.M.Ş."
  const rcdText3 = data?.kolonDetails?.rcd?.text3 || "(45-63)"

  // Çıkış sayısı: paneller (en az 1)
  const countOfOut = Math.max(allOutputs.length, 1);

  const layers = {
    0: { name: '0', frozen: false, visible: true, colorIndex: 7, color: 16777215 },
    18: { name: '18', frozen: false, visible: true, colorIndex: 1, color: 16711680 },
    AKSYAZI: { name: 'AKSYAZI', frozen: false, visible: true, colorIndex: 9, color: 12632256 }
  };

  const layer = layers.AKSYAZI;
  const options = {
    color: layer.color,
    colorIndex: layer.colorIndex,
    layer: layer.name,
    shape: true,
    hasContinuousLinetypePattern: false
  };
  const redOptions = { ...options, colorIndex: 1, color: 0xFF0000 };
  const blueFrameOptions = { ...options, colorIndex: 4, color: 0x0000FF };
  const dashedBlueFrameOptions = { ...blueFrameOptions, linetype: 'DASHED' }; // Kesikli mavi çizgi
  const dashedGreenFrameOptions = { ...options, colorIndex: 3, color: 0x00FF00, linetype: 'DASHED' }; // Kesikli yeşil çizgi

  const textHeight = 10 * Scl;
  const offsetOuts = 40 * Scl; // Çıkışlar arası dikey mesafe

  // ============== 1. ÖN HESAPLAMA: TekHatSema boyutlarını hesapla ==============
  const tekHatSemaSizes = [];
  let totalTsH = 0; // Toplam TekHatSema yüksekliği
  let maxTsW = 0;   // En geniş TekHatSema

  allOutputs.forEach((output, index) => {
    const breakerText = `${output.breaker?.curve || 'C'}-4x${output.breaker?.current || 40}A`;
    const tekHatData = {
      branchies: output.branchCircuits || [],
      feeder: output.feeder || {
        components: [
          { symbolCode: "CABLE", text: "4x6 mm² NYM" },
          { symbolCode: "AOS1P", text1: breakerText, text2: "10kA" },
          { symbolCode: "KAKR", text1: "30mA", text2: "K.A.K.R.", text3: "(25-32)A" }
        ]
      },
      panelName: output.name
    };

    // Sadece boyut almak için çağır (x, y vermeden)
    const tekHatResult = createTekHatSema(tekHatData);
    const tsW = tekHatResult?.width || 400 * Scl;
    const tsH = tekHatResult?.height || 300 * Scl;

    tekHatSemaSizes.push({ width: tsW, height: tsH, data: tekHatData });
    totalTsH += tsH;
    if (tsW > maxTsW) maxTsW = tsW;
  });

  // ============== 2. BOYUT HESAPLAMA ==============
  // KolonŞeması sabit boyutları (KsW, KsH)
  const KsW = 900 * Scl;  // KolonŞeması genişliği (bara dahil)
  const KsH = 400 * Scl;  // KolonŞeması yüksekliği

  // TekHatSema çizim alanı döndürülen height'tan daha büyük olabilir
  // Alt ve üst margin ekle (panel çıkış sayıları arttıkça yükseklik de artar)
  const tekHatMargin = 150 * Scl;  // Alt/üst margin

  // Doküman boyutları
  const docW = KsW + maxTsW;           // docW = KsW + TsW
  const docH = Math.max(totalTsH + tekHatMargin, KsH); // TekHatSema alanı + margin (en az KsH)

  // ============== 3. KOLONŞEMASİ YERLEŞİMİ ==============
  // KolonŞeması Y ekseninde docH/2'de ortalanmalı
  const kolonYOffset = docH / 2;

  // Ana çerçeve referans noktaları (yOffset uygulanmış)
  const mainFrameX = 50 * Scl;
  const mainFrameY = kolonYOffset - KsH / 2; // KolonŞeması alt kenarı

  // ADP bilgileri
  const adpNode = data?.adp || {};
  const adpInstalledPower = adpNode.installedTotalPower || 44174;
  const adpDemandPower = adpNode.demandPower || 19270;

  // Ana başlık - Mühürlü bölme (mavi çerçevenin üstünde)
  textIdCounter++;
  let text = {
    text: "Mühürlü bölme",
    x: mainFrameX + 465 * Scl,
    y: mainFrameY + 340 * Scl,
    textHeight: 14 * Scl,
    id: `muhurlu_bolme_title_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Sayaç Pano ve ADP yazıları (200 birim sağa kaydırıldı: 465+200=665, 495+200=695)
  textIdCounter++;
  text = {
    text: "Sayaç Pano",
    x: mainFrameX + 665 * Scl,
    y: mainFrameY + 340 * Scl,
    textHeight: 14 * Scl,
    id: `sayac_panosu_title_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  textIdCounter++;
  text = {
    text: "A.D.P.",
    x: mainFrameX + 695 * Scl,
    y: mainFrameY + 320 * Scl,
    textHeight: 14 * Scl,
    id: `adp_title_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Kurulu Güç ve Talep Güç (75 birim sola, 50 birim yukarı)
  textIdCounter++;
  text = {
    text: `Kurulu Güç:${adpInstalledPower} W`,
    x: mainFrameX + 295 * Scl,
    y: mainFrameY + 350 * Scl,
    textHeight: 12 * Scl,
    id: `kurulu_guc_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  textIdCounter++;
  text = {
    text: `Talep Güç: ${adpDemandPower} W`,
    x: mainFrameX + 295 * Scl,
    y: mainFrameY + 335 * Scl,
    textHeight: 12 * Scl,
    id: `talep_guc_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Mavi çerçeve (sayaç panosu kutusu) - kesikli çizgi
  const frameX = mainFrameX + 200 * Scl;
  const frameY = mainFrameY + 50 * Scl;
  const blueframeX = mainFrameX + 300 * Scl;
  const blueframeY = mainFrameY + 210 * Scl;
  result.push(Rectangle(blueframeX + -10, blueframeY, 330 * Scl, 120 * Scl, dashedBlueFrameOptions));

  // Büyük yeşil çerçeve (mavi çerçeveden 10 sol, 165 yukarı (40+125), 575x220, aşağı doğru)
  const bigFrameX = blueframeX - 20 * Scl;  // Mavi çerçeveden 10 birim sol
  const bigFrameY = blueframeY + 165 * Scl - 220 * Scl;  // 165 yukarıdan başla (40+125), 220 aşağı git (alt köşe)
  result.push(Rectangle(bigFrameX, bigFrameY, 575 * Scl, 220 * Scl, dashedGreenFrameOptions));

  // YEDAŞ kutusu
  const redFrameX = mainFrameX + 20 * Scl;
  const redFrameY = mainFrameY + 240 * Scl;
  const redFrameWidth = 60 * Scl;
  const redFrameHeight = 20 * Scl;
  result.push(Rectangle(redFrameX, redFrameY, redFrameWidth, redFrameHeight, blueFrameOptions));
  textIdCounter++;
  text = {
    text: "YEDAŞ",
    x: redFrameX + 13 * Scl,
    y: redFrameY + 10 * Scl,
    textHeight: textHeight,
    id: `yedas_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Kablo bilgisi (YEDAŞ sonrası)
  textIdCounter++;
  text = {
    text: cableText + "   15 m",
    x: frameX + -70 * Scl,
    y: frameY + 205 * Scl,
    textHeight: textHeight,
    id: `${cableText}_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Boru sembolü
  result.push(...Boru(frameX + 60 * Scl, frameY + 200 * Scl, 150 * Scl, Scl, 180, options));
  textIdCounter++;
  text = {
    text: "φ=100 Pe (kangal) boru",
    x: frameX + -20 * Scl,
    y: frameY + 170 * Scl,
    textHeight: 8 * Scl,
    id: `boru_kangal_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Ana kesici (YEDAŞ sonrası)
  result.push(...AnahtarliOtomatikSigorta(frameX + 118 * Scl, frameY + 200 * Scl, Scl, 3, 0, options));
  textIdCounter++;
  text = {
    text: breakerText1 + "/" + breakerText2,
    x: frameX + 90 * Scl,
    y: frameY + 215 * Scl,
    textHeight: textHeight,
    id: `${breakerText1}_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Sayaç sembolü
  result.push(...Sayac(frameX + 360 * Scl, frameY + 240 * Scl, Scl, 3, 0, options));

  // 63A 4P/300 mA - Y.K.Ş.
  textIdCounter++;
  text = {
    text: rcdText1,
    x: frameX + 210 * Scl,
    y: frameY + 240 * Scl,
    textHeight: textHeight,
    id: `${rcdText1}_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Y.K'lı T.M.Ş
  textIdCounter++;
  text = {
    text: rcdText2,
    x: frameX + 210 * Scl,
    y: frameY + 230 * Scl,
    textHeight: textHeight,
    id: `${rcdText2}_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // (45-63)
  textIdCounter++;
  text = {
    text: rcdText3,
    x: frameX + 220 * Scl,
    y: frameY + 220 * Scl,
    textHeight: textHeight,
    id: `${rcdText3}_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Kaçak akım koruma rölesi sembolü
  result.push(...KacakAkim(frameX + 240 * Scl, frameY + 200 * Scl, Scl, 0, options));

  // Bağlantı çizgileri (YEDAŞ -> Kesici -> Sayaç -> K.A.K.R.)
  result.push(PolarLine(frameX + 250 * Scl, frameY + 200 * Scl, 0, 100 * Scl, options));
  result.push(PolarLine(frameX + 130 * Scl, frameY + 200 * Scl, 0, 100 * Scl, options));
  result.push(PolarLine(frameX + -120 * Scl, frameY + 200 * Scl, 0, 226 * Scl, options));
  result.push(PolarLine(frameX + 360 * Scl, frameY + 80 * Scl, 0, 80 * Scl, options));

  // Dikey bağlantı çizgisi (aşağı doğru - topraklama için)
  const verticalLineX = frameX + 200 * Scl;
  result.push(PolarLine(verticalLineX + 170 * Scl, frameY + 220 * Scl, -90, 20 * Scl, options));
  result.push(PolarLine(verticalLineX + 150 * Scl, frameY + 220 * Scl, -90, 20 * Scl, options));

  // ============== TOPRAKLAMA BÖLÜMÜ ==============
  const listStartY = frameY + 130 * Scl;  // 50 birim aşağı (180 -> 130)
  const listX = mainFrameX + 100 * Scl;

  // Topraklama elemanları
  const groundingItems = data?.grounding || SABIT_DEGERLER.grounding || [];
  const { positions: groundingPositions, textSizes: groundingTextSizes } = calculateDynamicPositions(groundingItems.length, Scl);

  const dotX = verticalLineX + 160 * Scl;

  groundingItems.forEach((item, index) => {
    const textY = listStartY + groundingPositions[index];
    const lineY = textY - 5 * Scl;

    // Text çiz
    textIdCounter++;
    text = {
      text: item,
      x: listX + 250 * Scl,
      y: textY,
      textHeight: groundingTextSizes[index],
      id: `grounding_${index}_${textIdCounter}`
    };

    text.handle = text.id;
    result.push(Text(text.x, text.y, text, 0, options));

    // Yatay bağlantı çizgisi
    result.push(PolarLine(
      dotX,
      lineY,
      180,
      220 * Scl, // Çizgi uzunluğu
      options
    ));

    // Kırmızı nokta
    result.push(...YuvarlakSolid(
      dotX,
      lineY,
      3 * Scl,
      Scl,
      redOptions
    ));
  });

  if (groundingItems.length > 0) {
    const lastTextY = listStartY + groundingPositions[groundingPositions.length - 1];
    const lastLineY = lastTextY - 5 * Scl;
    const dynamicVerticalLength = (frameY + 220 * Scl) - lastLineY;
    result.push(PolarLine(verticalLineX + 160 * Scl, frameY + 220 * Scl, -90, dynamicVerticalLength, options));
  }

  // Topraklama bilgi metinleri
  const rightX = mainFrameX + 600 * Scl;
  const rightY = listStartY;

  textIdCounter++;
  text = {
    text: "Bağlantı filizi",
    x: rightX + -30 * Scl,
    y: rightY + -45 * Scl,
    textHeight: textHeight,
    id: `baglanti_filizi_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  textIdCounter++;
  text = {
    text: "30.5 Galvanizli",
    x: rightX + -30 * Scl,
    y: rightY + -60 * Scl,
    textHeight: textHeight,
    id: `galvanizli_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  textIdCounter++;
  text = {
    text: "Çelik şerit",
    x: rightX + -30 * Scl,
    y: rightY + -70 * Scl,
    textHeight: textHeight,
    id: `celik_serit_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Topraklama çubukları bilgisi
  textIdCounter++;
  text = {
    text: "1.5 m, 20 mm Çapında Bakır Topraklama",
    x: rightX + -30 * Scl,
    y: rightY + -110 * Scl,
    textHeight: textHeight,
    id: `topraklama_cubuk1_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  textIdCounter++;
  text = {
    text: "Çubukları",
    x: rightX + -30 * Scl,
    y: rightY + -125 * Scl,
    textHeight: textHeight,
    id: `topraklama_cubuk2_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // ============== A NOKTASI - DAĞITIM BARASI ==============
  // A noktası: Sayaç panosunun sağ tarafı
  const aPointX = 580 * Scl;
  const aPointY = frameY + 200 * Scl;

  // 1. A noktasından yatay hat (40 birim sağdan başla, 150 birim uzunluk)
  const hatBaslangicOffset = 40 * Scl;
  const hatUzunlugu1 = 150 * Scl;
  result.push(PolarLine(aPointX + hatBaslangicOffset, aPointY, 0, hatUzunlugu1, options));

  // 2. Kablo bilgisi (dağıtım barası öncesi)
  textIdCounter++;
  text = {
    text: "4x16 +10 mm² NYM",
    x: aPointX + hatBaslangicOffset + 10 * Scl,
    y: aPointY + 15 * Scl,
    textHeight: textHeight,
    id: `dagitim_kablo_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // 3. AOS (Anahtarlı Otomatik Sigorta) - 12 birim sağa kaydırılmış
  const aosOffset = 12 * Scl; // AOS genişliğinin yarısı kadar sağa kayma
  const aosX = aPointX + hatBaslangicOffset + hatUzunlugu1 + aosOffset;
  result.push(...AnahtarliOtomatikSigorta(aosX, aPointY, Scl, 3, 0, options));

  // AOS bilgisi (10 birim yukarı)
  textIdCounter++;
  text = {
    text: "C-4x63A",
    x: aosX - 15 * Scl,
    y: aPointY + 30 * Scl,
    textHeight: textHeight,
    id: `aos_text1_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  textIdCounter++;
  text = {
    text: "10kA",
    x: aosX + 5 * Scl,
    y: aPointY + 18 * Scl,
    textHeight: textHeight,
    id: `aos_text2_${textIdCounter}`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // 4. AOS'tan Main Panel'e yatay çizgi
  const hatUzunlugu2 = 42 * Scl;
  result.push(PolarLine(aosX + aosOffset, aPointY, 0, hatUzunlugu2, options));

  // ============== 5. MAIN PANEL (A.D.P.) SEMBOLÜ ==============
  // Panel boyutları - TekHatSema'daki gibi çıkış sayısına göre
  const cikisSayisi = allOutputs.length || 1;
  const mainPanoW = 50 * Scl;
  const mainPanoH = 20 * Scl + cikisSayisi * 20 * Scl;

  // Panel pozisyonu
  const mainPanelX = aosX + aosOffset + hatUzunlugu2 + mainPanoW / 2;
  const mainPanelY = aPointY;

  // Panel sembolü çiz (PanoAnaTali)
  result.push(...PanoAnaTali(mainPanelX, mainPanelY, mainPanoW, mainPanoH, Scl, 0, options));

  // ============== 6. DİKEY BARA ==============
  // Bara uzunluğu hesapla: (çıkış sayısı - 1) * 40 + 40
  const baraLength = (cikisSayisi - 1) * 40 * Scl + 40 * Scl; // Minimum 40 birim

  // Bara X pozisyonu = Panelden 80 birim sağda
  const baraX = mainPanelX + mainPanoW / 2 + 80 * Scl;
  const baraCenterY = aPointY; // Bara merkezi ana hat seviyesinde

  // Bara noktaları (ortalanmış)
  const P1 = { x: baraX, y: baraCenterY - baraLength / 2 }; // Alt nokta
  const P2 = { x: baraX, y: baraCenterY + baraLength / 2 }; // Üst nokta

  // Dikey kalın BARA çiz (kırmızı, 3 çizgi yan yana kalınlık için)
  result.push(PolarLine(P1.x - 2 * Scl, P2.y, -90, baraLength, redOptions));
  result.push(PolarLine(P1.x, P2.y, -90, baraLength, redOptions));
  result.push(PolarLine(P1.x + 2 * Scl, P2.y, -90, baraLength, redOptions));

  // MainPanel'den bara'ya tek yatay çizgi
  const mainPanelRightX = mainPanelX + mainPanoW / 2;
  result.push(PolarLine(mainPanelRightX, aPointY, 0, baraX - mainPanelRightX, options));

  // ============== 7. PANEL ÇIKIŞLARI ==============
  const kisaCizgi = 30 * Scl; // Baradan AOS'a kısa çizgi

  // ============== 4. TEK HAT ŞEMALARI YERLEŞİMİ ==============
  // TekHatŞemaları dokümanın üstünden başlayarak aşağı doğru yerleştirilecek
  const tekHatStartY = docH;

  // Polyline en sağ noktasını (polylineMidX) ön-hesapla → tekHatSemaX buna hizalanacak
  const baraOutputEndX = baraX + 2 * Scl + kisaCizgi + 2 * aosOffset; // AOS sağ ucu
  let preM = 0;
  let preCumH = 0;
  let maxPolylineMidX = baraOutputEndX;
  allOutputs.forEach((output, index) => {
    const preTekHatEntryY = tekHatStartY - preCumH - 200 * Scl;
    if (preTekHatEntryY > kolonYOffset) preM++;
    const pmx = baraOutputEndX + 30 * Scl + preM * 20 * Scl - (index - preM + 1) * 20 * Scl;
    if (pmx > maxPolylineMidX) maxPolylineMidX = pmx;
    preCumH += tekHatSemaSizes[index].height;
  });

  // TekHat besleme hattı başlangıcı (startOffset=40) polyline en sağ noktasına hizalı
  const tekHatSemaX = maxPolylineMidX - 40 * Scl;
  let cumulativeHeight = 0;

  // Polyline X hesaplaması için: KolonŞemasının üstündeki TekHatŞeması sayacı
  let m = 0;

  allOutputs.forEach((output, index) => {
    // Bara üzerindeki çıkış noktası (ters sıra - TekHatŞeması ile eşleşmesi için)
    const reverseIndex = allOutputs.length - 1 - index;
    const baraOutputY = P1.y + 20 * Scl + reverseIndex * 40 * Scl;

    // Kırmızı nokta (bara üzerinde)
    result.push(...YuvarlakSolid(baraX, baraOutputY, 4 * Scl, Scl, redOptions));

    // 4. Bara'dan çıkış için kullanılacak Y
    const outputY = baraOutputY;

    // Baradan kısa çizgi
    result.push(PolarLine(baraX + 2 * Scl, outputY, 0, kisaCizgi, options));

    // AOS (çıkış kesicisi) - 12 birim sağa kaydırılmış
    const outputAosX = baraX + 2 * Scl + kisaCizgi + aosOffset;
    result.push(...AnahtarliOtomatikSigorta(outputAosX, outputY, Scl, 3, 0, options));

    // Kesici bilgisi - 40 birim sola, 10 birim yukarı
    const breakerText = `${output.breaker?.curve || 'C'}-4x${output.breaker?.current || 40}A`;
    textIdCounter++;
    text = {
      text: breakerText,
      x: outputAosX + aosOffset - 40 * Scl,
      y: outputY + 15 * Scl,
      textHeight: textHeight,
      id: `output_breaker_${index}_${textIdCounter}`
    };
    text.handle = text.id;
    result.push(Text(text.x, text.y, text, 0, options));

    // ============== TEK HAT ŞEMASI EKLE ==============
    const tsSize = tekHatSemaSizes[index];

    // Panel için TekHatSema verisi hazırla
    // TekHatSema'nın default Y pozisyonu yaklaşık: 20 * branchCount + 220
    // Bu değeri tekHatStartY - cumulativeHeight konumuna taşımak için gerekli offset hesapla
    const branchCount = output.branchCircuits?.length || 0;
    const estimatedDefaultY = 20 * branchCount + 220;  // TekHatSema'nın hesaplanan default Y'si
    const desiredY = tekHatStartY - cumulativeHeight;  // İstenen Y pozisyonu (üstten aşağı)
    const calculatedYOffset = desiredY - estimatedDefaultY;

    // ============== AOS'tan TekHatŞemasına POLYLINE BAĞLANTISI ==============
    // AOS sağ tarafından başla, sonra dikey olarak panel besleme kablosu Y'sine, sonra yatayda TekHatŞeması X'ine
    const polylineStartX = outputAosX + aosOffset;  // AOS sağ tarafı
    const tekHatEntryX = tekHatSemaX + 40 * Scl;  // TekHatŞeması giriş X'i (boundingBox.startOffset = 40)
    const tekHatEntryY = desiredY - 200 * Scl;  // Panel besleme kablosu Y seviyesi (Y -= 200 generateTekHatSema'da)

    // TekHatŞeması KolonŞemasının üstünde mi kontrol et
    const isAboveKolon = tekHatEntryY > kolonYOffset;
    if (isAboveKolon) {
      m++;  // Yukarıdaki TekHatŞeması sayısını artır
    }

    // Polyline X hesapla: x = 30 + m*20 - (i-m+1)*20
    // Yukarıdakiler sağa doğru açılır, aşağıdakiler sola doğru daralır
    const polylineMidX = polylineStartX + 30 * Scl + m * 20 * Scl - (index - m + 1) * 20 * Scl;

    // Polyline açık kalmalı (shape: false)
    const openPolylineOptions = { ...options, shape: false };

    const polylineVertices = [
      { x: polylineStartX, y: outputY, z: 0 },           // AOS sağ tarafı
      { x: polylineMidX, y: outputY, z: 0 },             // Yatay hareket (30 + i*20)
      { x: polylineMidX, y: tekHatEntryY, z: 0 },        // Dikey hareket (panel besleme kablosu Y'sine)
      { x: tekHatEntryX, y: tekHatEntryY, z: 0 }         // Yatay hareket (TekHatŞeması X'ine)
    ];
    result.push(PolyLine(0, 0, polylineVertices, openPolylineOptions));

    const tekHatData = {
      x: tekHatSemaX,
      yOffset: calculatedYOffset,  // Doğru pozisyona taşımak için hesaplanan offset
      branchies: output.branchCircuits || [],
      feeder: output.feeder || {
        components: [
          { symbolCode: "CABLE", text: "4x6 mm² NYM" },
          { symbolCode: "AOS1P", text1: breakerText, text2: "10kA" },
          { symbolCode: "KAKR", text1: "30mA", text2: "K.A.K.R.", text3: "(25-32)A" }
        ]
      },
      panelName: output.name
    };

    // TekHatSema oluştur
    const tekHatResult = createTekHatSema(tekHatData);

    if (tekHatResult && tekHatResult.result && tekHatResult.result.length > 0) {
      // TekHatSema sonuçlarını ana result'a ekle
      result.push(...tekHatResult.result);
    }

    // Sonraki TekHatŞeması için kümülatif yüksekliği güncelle
    cumulativeHeight += tsSize.height;
  });

  // ============== ÇERÇEVE VE BAŞLIK ==============
  const kolonSemaWidth = tekHatSemaX + maxTsW;
  const kolonSemaHeight = docH;

  const headerTextWidth = 235 * Scl;
  text = {
    text: "KOLON ŞEMASI",
    x: kolonSemaWidth / 2 - headerTextWidth / 2,
    y: kolonSemaHeight + headerHeight * 0.3,
    textHeight: 22.5 * Scl,
    id: `title_text_header_KolonSemaMulti`
  };
  text.handle = text.id;
  result.push(Text(text.x, text.y, text, 0, options));

  // Dış çerçeve
  const vertices = [
    { x: 0, y: 0, z: 0 },
    { x: 0, y: kolonSemaHeight + headerHeight, z: 0 },
    { x: kolonSemaWidth, y: kolonSemaHeight + headerHeight, z: 0 },
    { x: kolonSemaWidth, y: 0, z: 0 },
  ]
  options.shape = true
  result.push(PolyLine(0, 0, vertices, options))
  result.push(PolarLine(0, kolonSemaHeight, 0, kolonSemaWidth, options));

  return {
    result: result,
    width: kolonSemaWidth,
    height: kolonSemaHeight + headerHeight
  };
};

export default createKolonSemaMultiFloor;
